import {
  AuditOutlined,
  BookOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  DoubleRightOutlined,
  EllipsisOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  FormOutlined,
  HighlightOutlined,
  MailOutlined,
  MinusSquareFilled,
  RetweetOutlined,
  RollbackOutlined,
  SnippetsOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';

import useAPIQuery from '../../api/useAPIQuery';
import CandidateWhatsAppLabel from '../../components/CandidateWhatsAppLabel';
import { CandidateCollection } from '../../types';

interface ApplicantAnalysisTableActionsProps {
  candidateId: CandidateCollection['id'];
  candidateName: CandidateCollection['name'];
  hasViewed: boolean;
  disableSendAdditionalQuestions?: boolean;
  disableSendIntegrationAdmission?: boolean;
  reject?: boolean;
  onRejectClick?: () => void;
  moveStage?: boolean;
  onMoveStageClick?: () => void;
  rollback?: boolean;
  onRollbackClick?: () => void;
  givenUp?: boolean;
  onGiveUpClick?: () => void;
  select?: boolean;
  onSelectClick?: () => void;
  sendAnnotation?: boolean;
  onSendAnnotationClick?: () => void;
  startInterview?: boolean;
  disabledInterviewTooltipText?: string;
  onStartInterviewClick?: () => void;
  registerOpinion?: boolean;
  disabledOpinionText?: string;
  onRegisterOpinionClick?: () => void;
  sendEmail?: boolean;
  onSendEmailClick?: () => void;
  sendTests?: boolean;
  onSendTestsClick?: () => void;
  sendCustomTests?: boolean;
  onSendCustomTestsClick?: () => void;
  sendAdditionalQuestions?: boolean;
  onSendAdditionalQuestionsClick?: () => void;
  markAsUnviewed?: boolean;
  onMarkAsUnviewedClick?: () => void;
  sendIntegrationAdmission?: boolean;
  setSendIntegrationAdmission?: () => void;
  sendWhatsApp?: boolean;
  sendPracticalTechnicalCase?: boolean;
  onSendPracticalTechnicalCaseClick?: () => void;
}

const ApplicantAnalysisTableActions = ({
  candidateId,
  candidateName,
  hasViewed,
  disableSendAdditionalQuestions,
  disableSendIntegrationAdmission,
  reject,
  onRejectClick,
  moveStage,
  onMoveStageClick,
  rollback,
  onRollbackClick,
  givenUp,
  onGiveUpClick,
  select,
  onSelectClick,
  sendAnnotation,
  onSendAnnotationClick,
  startInterview,
  disabledInterviewTooltipText,
  onStartInterviewClick,
  registerOpinion,
  disabledOpinionText,
  onRegisterOpinionClick,
  sendEmail,
  onSendEmailClick,
  sendTests,
  onSendTestsClick,
  sendCustomTests,
  onSendCustomTestsClick,
  sendAdditionalQuestions,
  onSendAdditionalQuestionsClick,
  markAsUnviewed,
  onMarkAsUnviewedClick,
  sendIntegrationAdmission,
  setSendIntegrationAdmission,
  sendWhatsApp,
  sendPracticalTechnicalCase,
  onSendPracticalTechnicalCaseClick,
}: ApplicantAnalysisTableActionsProps) => {
  // useAPIQuery that only gets data from the cache
  const cachedCandidate = useAPIQuery(`/candidates/${candidateId}/`, {
    enabled: false,
  });

  return (
    <Dropdown
      menu={{
        onClick: ({ key }) => {
          switch (key) {
            case 'stage':
              moveStage && onMoveStageClick?.();
              break;
            case 'reject':
              reject && onRejectClick?.();
              break;
            case 'rollback':
              rollback && onRollbackClick?.();
              break;
            case 'giveUp':
              givenUp && onGiveUpClick?.();
              break;
            case 'select':
              select && onSelectClick?.();
              break;
            case 'annotation':
              sendAnnotation && onSendAnnotationClick?.();
              break;
            case 'email':
              sendEmail && onSendEmailClick?.();
              break;
            case 'tests':
              sendTests && onSendTestsClick?.();
              break;
            case 'custom-tests':
              sendCustomTests && onSendCustomTestsClick?.();
              break;
            case 'additional_questions':
              sendAdditionalQuestions && onSendAdditionalQuestionsClick?.();
              break;
            case 'send-integration-admission':
              sendIntegrationAdmission && setSendIntegrationAdmission?.();
              break;
            case 'mark-as-unviewed':
              markAsUnviewed && onMarkAsUnviewedClick?.();
              break;
            case 'start-interview':
              startInterview && onStartInterviewClick?.();
              break;
            case 'register-opinion':
              registerOpinion && onRegisterOpinionClick?.();
              break;
            case 'practical-technical-case':
              sendPracticalTechnicalCase && onSendPracticalTechnicalCaseClick?.();
              break;
          }
        },
        items: [
          moveStage
            ? { key: 'stage', icon: <DoubleRightOutlined />, label: 'Mover de etapa' }
            : null,
          reject ? { key: 'reject', icon: <MinusSquareFilled />, label: 'Reprovar' } : null,
          rollback
            ? { key: 'rollback', icon: <RollbackOutlined />, label: 'Retornar candidato' }
            : null,
          moveStage || reject || rollback ? { type: 'divider' as const } : null,
          sendIntegrationAdmission
            ? {
                key: 'send-integration-admission',
                icon: <RetweetOutlined />,
                label: (
                  <Tooltip
                    title={
                      disableSendIntegrationAdmission ? 'Já há uma admissão sendo enviada' : ''
                    }
                  >
                    Enviar admissão
                  </Tooltip>
                ),
                disabled: disableSendIntegrationAdmission,
              }
            : null,
          givenUp
            ? {
                key: 'giveUp',
                icon: <CloseCircleOutlined />,
                label: 'Marcar desistente',
              }
            : null,
          select
            ? { key: 'select', icon: <DoubleRightOutlined />, label: 'Adicionar à vaga' }
            : null,
          sendAnnotation
            ? { key: 'annotation', icon: <FormOutlined />, label: 'Registrar comentário' }
            : null,
          startInterview
            ? {
                key: 'start-interview',
                icon: <HighlightOutlined />,
                disabled: Boolean(disabledInterviewTooltipText),
                label: (
                  <Tooltip title={disabledInterviewTooltipText || ''}>Iniciar entrevista</Tooltip>
                ),
              }
            : null,
          registerOpinion
            ? {
                key: 'register-opinion',
                icon: <HighlightOutlined />,
                disabled: Boolean(disabledOpinionText),
                label: <Tooltip title={disabledOpinionText || ''}>Registrar parecer</Tooltip>,
              }
            : null,
          sendEmail ||
          sendAdditionalQuestions ||
          sendCustomTests ||
          sendTests ||
          sendPracticalTechnicalCase
            ? {
                key: 'send',
                label: 'Enviar e-mail',
                icon: <MailOutlined />,
                children: [
                  sendEmail
                    ? { key: 'email', icon: <CommentOutlined />, label: 'Enviar comunicação' }
                    : null,
                  sendTests
                    ? { key: 'tests', icon: <AuditOutlined />, label: 'Enviar testes' }
                    : null,
                  sendCustomTests
                    ? {
                        key: 'custom-tests',
                        icon: <BookOutlined />,
                        label: 'Enviar testes customizados',
                      }
                    : null,
                  sendAdditionalQuestions
                    ? {
                        key: 'additional_questions',
                        icon: <FileTextOutlined />,
                        disabled: !!disableSendAdditionalQuestions,
                        label: (
                          <Tooltip
                            title={
                              disableSendAdditionalQuestions
                                ? 'Os candidatos já responderam as perguntas de pré-requisito'
                                : ''
                            }
                          >
                            Enviar perguntas de pré-requisito
                          </Tooltip>
                        ),
                      }
                    : null,
                  sendPracticalTechnicalCase
                    ? {
                        key: 'practical-technical-case',
                        icon: <SnippetsOutlined />,
                        label: 'Enviar case',
                      }
                    : null,
                ],
              }
            : null,
          sendWhatsApp
            ? {
                key: 'whatsapp',
                icon: <WhatsAppOutlined />,
                disabled: !cachedCandidate.data?.phone_contacts?.find(
                  (phoneContact) => phoneContact.type === 'PERSONAL'
                ),
                label: <CandidateWhatsAppLabel candidateId={candidateId} />,
              }
            : null,
          markAsUnviewed
            ? {
                key: 'mark-as-unviewed',
                icon: <FolderOpenOutlined />,
                disabled: !hasViewed,
                label: 'Marcar como não visto',
              }
            : null,
        ],
      }}
    >
      <Button type="text" icon={<EllipsisOutlined />} aria-label={`Ações para ${candidateName}`} />
    </Dropdown>
  );
};

export default ApplicantAnalysisTableActions;
